import { Card, CardContent } from '@/common/ui/card'
import { Skeleton } from '@/common/ui/skeleton'
import { Eye, Play, TrendingUp, Wallet } from 'lucide-react'

interface BrokeFlexAnalyticsOverviewCardsProps {
	overall?: {
		totalRecipients: number
		uniqueOpeners: number
		totalOpens: number
		uniquePlayers: number
		totalPlays: number
		openRate: number
		playRate: number
		avgHoursToOpen: number
		avgHoursToPlay: number
		depositConversions: number
		totalDepositAmount: number
	}
	isLoading?: boolean
}

export default function BrokeFlexAnalyticsOverviewCards({
	overall,
	isLoading,
}: BrokeFlexAnalyticsOverviewCardsProps) {
	if (isLoading) {
		return (
			<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
				{Array.from({ length: 8 }).map((_, i) => (
					<Card key={i}>
						<CardContent className="p-6">
							<Skeleton className="h-20 w-full" />
						</CardContent>
					</Card>
				))}
			</div>
		)
	}

	if (!overall) {
		return (
			<div className="rounded-lg border border-dashed p-8 text-center">
				<p className="text-muted-foreground">ไม่มีข้อมูลสถิติ</p>
			</div>
		)
	}

	const formatNumber = (num: number) => {
		return new Intl.NumberFormat('th-TH').format(num)
	}

	const formatCurrency = (num: number) => {
		return new Intl.NumberFormat('th-TH', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(num)
	}

	const formatHours = (hours: number) => {
		if (hours === 0) return '0 ชม.'
		if (hours < 1) return `${Math.round(hours * 60)} นาที`
		if (hours < 24) return `${hours.toFixed(1)} ชม.`
		return `${(hours / 24).toFixed(1)} วัน`
	}

	return (
		<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
			{/* Total Recipients */}
			<Card>
				<CardContent className="p-6">
					<div className="flex items-center justify-between">
						<div>
							<p className="text-sm font-medium text-muted-foreground">
								จำนวนผู้รับทั้งหมด
							</p>
							<p className="text-2xl font-bold">
								{formatNumber(overall.totalRecipients)}
							</p>
						</div>
						<div className="rounded-lg bg-blue-100 p-3 dark:bg-blue-900/20">
							<Eye className="h-5 w-5 text-blue-600 dark:text-blue-400" />
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Open Rate */}
			<Card>
				<CardContent className="p-6">
					<div className="flex items-center justify-between">
						<div>
							<p className="text-sm font-medium text-muted-foreground">
								Open Rate
							</p>
							<p className="text-2xl font-bold">
								{overall.openRate.toFixed(1)}%
							</p>
							<p className="text-xs text-muted-foreground">
								{formatNumber(overall.uniqueOpeners)} คน เปิดดู
							</p>
						</div>
						<div className="rounded-lg bg-green-100 p-3 dark:bg-green-900/20">
							<Eye className="h-5 w-5 text-green-600 dark:text-green-400" />
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Play Rate */}
			<Card>
				<CardContent className="p-6">
					<div className="flex items-center justify-between">
						<div>
							<p className="text-sm font-medium text-muted-foreground">
								Play Rate
							</p>
							<p className="text-2xl font-bold">
								{overall.playRate.toFixed(1)}%
							</p>
							<p className="text-xs text-muted-foreground">
								{formatNumber(overall.uniquePlayers)} คน เล่นเกม
							</p>
						</div>
						<div className="rounded-lg bg-purple-100 p-3 dark:bg-purple-900/20">
							<Play className="h-5 w-5 text-purple-600 dark:text-purple-400" />
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Deposit Conversions */}
			<Card>
				<CardContent className="p-6">
					<div className="flex items-center justify-between">
						<div>
							<p className="text-sm font-medium text-muted-foreground">
								Deposit Conversion
							</p>
							<p className="text-2xl font-bold">
								{formatNumber(overall.depositConversions)}
							</p>
							<p className="text-xs text-muted-foreground">
								{formatCurrency(overall.totalDepositAmount)} บาท
							</p>
						</div>
						<div className="rounded-lg bg-amber-100 p-3 dark:bg-amber-900/20">
							<Wallet className="h-5 w-5 text-amber-600 dark:text-amber-400" />
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Total Opens */}
			<Card>
				<CardContent className="p-6">
					<div className="flex items-center justify-between">
						<div>
							<p className="text-sm font-medium text-muted-foreground">
								จำนวนครั้งที่เปิดดู
							</p>
							<p className="text-2xl font-bold">
								{formatNumber(overall.totalOpens)}
							</p>
							<p className="text-xs text-muted-foreground">
								เฉลี่ย{' '}
								{(overall.totalOpens / overall.uniqueOpeners || 0).toFixed(1)}{' '}
								ครั้ง/คน
							</p>
						</div>
						<div className="rounded-lg bg-indigo-100 p-3 dark:bg-indigo-900/20">
							<Eye className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Total Plays */}
			<Card>
				<CardContent className="p-6">
					<div className="flex items-center justify-between">
						<div>
							<p className="text-sm font-medium text-muted-foreground">
								จำนวนครั้งที่เล่นเกม
							</p>
							<p className="text-2xl font-bold">
								{formatNumber(overall.totalPlays)}
							</p>
							<p className="text-xs text-muted-foreground">
								เฉลี่ย{' '}
								{(overall.totalPlays / overall.uniquePlayers || 0).toFixed(1)}{' '}
								ครั้ง/คน
							</p>
						</div>
						<div className="rounded-lg bg-pink-100 p-3 dark:bg-pink-900/20">
							<Play className="h-5 w-5 text-pink-600 dark:text-pink-400" />
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Average Hours to Open */}
			<Card>
				<CardContent className="p-6">
					<div className="flex items-center justify-between">
						<div>
							<p className="text-sm font-medium text-muted-foreground">
								เวลาเฉลี่ยที่เปิดดู
							</p>
							<p className="text-2xl font-bold">
								{formatHours(overall.avgHoursToOpen)}
							</p>
							<p className="text-xs text-muted-foreground">
								หลังจากได้รับข้อความ
							</p>
						</div>
						<div className="rounded-lg bg-cyan-100 p-3 dark:bg-cyan-900/20">
							<TrendingUp className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Average Hours to Play */}
			<Card>
				<CardContent className="p-6">
					<div className="flex items-center justify-between">
						<div>
							<p className="text-sm font-medium text-muted-foreground">
								เวลาเฉลี่ยที่เล่นเกม
							</p>
							<p className="text-2xl font-bold">
								{formatHours(overall.avgHoursToPlay)}
							</p>
							<p className="text-xs text-muted-foreground">
								หลังจากเปิดดูข้อความ
							</p>
						</div>
						<div className="rounded-lg bg-teal-100 p-3 dark:bg-teal-900/20">
							<TrendingUp className="h-5 w-5 text-teal-600 dark:text-teal-400" />
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	)
}
